import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useCreateBookmark, isQuotaError } from 'markcat-api-client';
import { PageSkeleton } from 'markcat-ui';
import { useAppOutlet } from '../routes/AppShell';
import { useUIStore } from '../stores/ui';

/** Extract the first http(s) URL from a string. Android Chrome usually drops the
 *  shared link into `text` (sometimes alongside the page title), not `url`. */
function firstUrl(s: string | null): string | null {
  if (!s) return null;
  const m = s.match(/https?:\/\/[^\s]+/i);
  return m ? m[0] : null;
}

/**
 * Web Share Target landing route (`/app/share`). Android opens the installed,
 * already-authenticated PWA here with the shared link in query params; we add it
 * via the normal create-bookmark flow, then bounce to the list. No backend
 * change — the call is the same authenticated POST the Add bar makes.
 */
export function Share() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useAppOutlet();
  const createBookmark = useCreateBookmark();
  // Share-target navigations can re-render; guard so we enqueue exactly once.
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;

    const url = params.get('url') || firstUrl(params.get('text')) || firstUrl(params.get('title'));
    if (!url) {
      showToast('Nothing to add — no link in the share', 'error');
      navigate('/app', { replace: true });
      return;
    }

    createBookmark.mutate(
      { url, source: 'web' },
      {
        onSuccess: () => {
          showToast('Bookmark added', 'success');
          navigate('/app', { replace: true });
        },
        onError: (err) => {
          const quota = isQuotaError(err);
          if (quota) {
            showToast(quota.message, 'error');
            navigate('/app/billing', { replace: true });
            return;
          }
          // Don't drop the shared link: park it for the manager, which
          // reopens it in the Add bar so the user can retry.
          useUIStore.getState().setPendingShareUrl(url);
          showToast('Couldn’t save that link — retry from the Add bar', 'error');
          navigate('/app', { replace: true });
        },
      },
    );
  }, [params, navigate, showToast, createBookmark]);

  return <PageSkeleton variant="manager" />;
}
